import axios from 'axios';

// Import trading permissions hook function (we'll import it as a function)
let checkTradingPermissions = null;

// Initialize permissions checker - ALWAYS ALLOW TRADING
const initializePermissionsChecker = () => {
  // Always allow all trading actions - no suitability test required
  checkTradingPermissions = () => ({ allowed: true });
};

// Initialize on module load
initializePermissionsChecker();

// Detect if accessing from mobile device and use appropriate API URL
const getApiUrl = () => {
  console.log('🔍 Detecting API URL for transactions...');
  console.log('🔍 Current hostname:', window.location.hostname);
  console.log('🔍 Current protocol:', window.location.protocol);
  console.log('🔍 Environment API URL:', process.env.REACT_APP_API_URL);
  
  // If env variable is set, use it
  if (process.env.REACT_APP_API_URL) {
    console.log('✅ Using env variable:', process.env.REACT_APP_API_URL);
    return process.env.REACT_APP_API_URL;
  }
  
  // If accessing from localhost, use localhost
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    console.log('✅ Using localhost for development');
    return 'http://localhost:8000';
  }
  
  // Production: Use same protocol and host as frontend
  const isHttps = window.location.protocol === 'https:';
  const productionUrl = `${isHttps ? 'https' : 'http'}://${window.location.hostname}`;
  console.log('✅ Using production URL:', productionUrl);
  return productionUrl;
};

const API_BASE_URL = getApiUrl() + '/api';
console.log('🔗 Transaction API URL:', API_BASE_URL);

class TransactionService {
  // Check trading permissions before API calls - ALWAYS ALLOW (backend decides)
  checkPermissions(action) {
    // Frontend always allows all actions - backend decides what's blocked
    console.log(`🔓 Frontend allowing ${action} - backend will decide restrictions`);
    return { allowed: true };
  }

  // Validate authentication before API calls
  validateAuth() {
    const token = localStorage.getItem('userToken');
    const userId = localStorage.getItem('userId');
    const userData = localStorage.getItem('userData');
    
    if (!token || !userId || !userData) {
      throw new Error('Authentication required. Please log in again.');
    }
    
    return { token, userId };
  }

  // Take a loan
  async takeLoan(userId, amount) {
    try {
      // Frontend always allows - backend will handle restrictions
      console.log('💰 Proceeding with loan request - backend will validate');
      
      // Validate authentication first
      const { token } = this.validateAuth();
      
      const url = `${API_BASE_URL}/transactions/loan/take`;
      const payload = { userId, amount };
      
      console.log('=== LOAN REQUEST DEBUG ===');
      console.log('URL:', url);
      console.log('Payload:', payload);
      console.log('Token exists:', !!token);
      console.log('User authenticated:', !!userId);
      
      const response = await axios.post(url, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Loan response successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ LOAN REQUEST ERROR:');
      console.error('Error type:', error.name);
      console.error('Error message:', error.message);
      console.error('Response status:', error.response?.status);
      console.error('Response data:', error.response?.data);
      
      // Handle specific error cases
      if (error.response?.status === 401 || error.response?.status === 403) {
        throw new Error('Authentication failed. Please log in again.');
      }
      
      if (error.response?.status === 429) {
        throw new Error('Too many requests. Please wait a moment and try again.');
      }
      
      // Return specific error message if available
      const errorMessage = error.response?.data?.message || error.message || 'Loan request failed';
      throw new Error(errorMessage);
    }
  }

  // Get portfolio data with retry logic
  async getPortfolio(userId) {
    const maxRetries = 3;
    
    // Validate authentication first
    const { token } = this.validateAuth();
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`📊 Fetching portfolio for user ${userId} (attempt ${attempt})`);
        
        const response = await axios.get(
          `${API_BASE_URL}/transactions/portfolio/${userId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        console.log('✅ Portfolio data retrieved successfully');
        return response.data;
      } catch (error) {
        console.error(`❌ Error fetching portfolio (attempt ${attempt}):`, error.response?.status, error.message);
        
        // Handle authentication errors immediately
        if (error.response?.status === 401 || error.response?.status === 403) {
          throw new Error('Authentication failed. Please log in again.');
        }
        
        // If it's a 429 error and we have retries left, wait and retry
        if (error.response?.status === 429 && attempt < maxRetries) {
          const waitTime = Math.pow(2, attempt) * 1000; // Exponential backoff
          console.log(`⏳ Rate limited, waiting ${waitTime}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          continue;
        }
        
        throw error.response?.data || error;
      }
    }
  }

  // Buy asset
  async buyAsset(userId, assetSymbol, quantity) {
    try {
      console.log('📈 Processing buy request');
      
      const response = await axios.post(
        `${API_BASE_URL}/transactions/buy`,
        {
          userId,
          assetSymbol,
          quantity
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('userToken')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error buying asset:', error);
      throw error.response?.data || error;
    }
  }

  // Sell asset
  async sellAsset(userId, assetSymbol, quantity) {
    try {
      console.log('📉 Processing sell request');
      
      const response = await axios.post(
        `${API_BASE_URL}/transactions/sell`,
        {
          userId,
          assetSymbol,
          quantity
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('userToken')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error selling asset:', error);
      throw error.response?.data || error;
    }
  }

  // Get asset price
  async getAssetPrice(symbol) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/transactions/asset/${symbol}/price`
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching asset price:', error);
      throw error.response?.data || error;
    }
  }

  // Buy asset with Polygon price
  async buyAssetWithPolygonPrice(userId, assetSymbol, quantity, polygonPrice) {
    try {
      console.log('📊 Processing Polygon buy request');
      
      const response = await axios.post(
        `${API_BASE_URL}/transactions/buy-polygon`,
        {
          userId,
          assetSymbol,
          quantity,
          polygonPrice
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('userToken')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error buying asset with Polygon price:', error);
      throw error.response?.data || error;
    }
  }

  // Sell asset with Polygon price
  async sellAssetWithPolygonPrice(userId, assetSymbol, quantity, polygonPrice) {
    try {
      // Frontend always allows - backend will handle restrictions
      console.log('📊 Proceeding with Polygon sell request - backend will validate');
      
      const response = await axios.post(
        `${API_BASE_URL}/transactions/sell-polygon`,
        {
          userId,
          assetSymbol,
          quantity,
          polygonPrice
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('userToken')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error selling asset with Polygon price:', error);
      throw error.response?.data || error;
    }
  }

  // Repay loan
  async repayLoan(userId, amount) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/transactions/loan/repay`,
        {
          userId,
          amount
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('userToken')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error repaying loan:', error);
      throw error.response?.data || error;
    }
  }

  // Open deposit (deposit money to savings)
  async openDeposit(userId, amount) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/transactions/deposit/open`,
        {
          userId,
          amount
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('userToken')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error opening deposit:', error);
      throw error.response?.data || error;
    }
  }

  // Close deposit (withdraw money from savings)
  async closeDeposit(userId, amount) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/transactions/deposit/close`,
        {
          userId,
          amount
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('userToken')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error closing deposit:', error);
      throw error.response?.data || error;
    }
  }
}

export default new TransactionService();